/**
 * Adapter hooks that bridge the WhatsApp-specific API hooks to the
 * generic InboxHooks interface expected by shared-inbox components.
 *
 * Each adapter wraps the real hook and maps the platform type to GenericConversation /
 * GenericMessage, keeping the original hooks untouched.
 */
import type {
  InboxHooks,
  GenericConversation,
  GenericMessage,
  GenericAgentConfig,
} from '@/components/shared-inbox/types';
import type { ConversationStatus } from '@/types/outreach';
import {
  useConversations as useWAConversations,
  useConversationDetail as useWAConversationDetail,
  useConversationMessages as useWAConversationMessages,
  useSendAdminMessage as useWASendAdminMessage,
  useUpdateConversationStatus as useWAUpdateConversationStatus,
  useMarkConversationRead as useWAMarkConversationRead,
  useAgentConfigs as useWAAgentConfigs,
  useSaveAgentConfig as useWASaveAgentConfig,
  useDeleteAgentConfig as useWADeleteAgentConfig,
  useToggleInterest as useWAToggleInterest,
  useToggleStar as useWAToggleStar,
} from './useWhatsappInbox';
import {
  useBlacklistConversation as useWABlacklistConversation,
  useUnblacklistConversation as useWAUnblacklistConversation,
} from './usePhoneBlacklist';

// ── Conversation list ────────────────────────────────────────────────────────

function useConversations(
  search?: string,
  status?: ConversationStatus | '',
  interested?: string,
  starred?: boolean,
) {
  const result = useWAConversations(search, status, interested, starred);
  return {
    ...result,
    data: result.data
      ? {
          ...result.data,
          pages: result.data.pages.map((page) => ({
            count: page.count,
            results: page.results.map(
              (c): GenericConversation => ({
                id: c.id,
                primaryId: c.customerPhone,
                secondaryId: null,
                customerName: c.customerName,
                profilePicUrl: null,
                status: c.status,
                unreadCount: c.unreadCount,
                updatedAt: c.updatedAt,
                place: c.place,
                person: c.person ? { id: c.person.id, isInterested: c.person.isInterested } : null,
                lastMessage: c.lastMessage,
                isWindowOpen: c.isWindowOpen,
                windowExpiresAt: c.windowExpiresAt,
                isStarred: c.isStarred,
                starredAt: c.starredAt,
                isPhoneBlacklisted: c.isPhoneBlacklisted ?? false,
                phoneBlacklistReason: c.phoneBlacklistReason ?? null,
              }),
            ),
          })),
        }
      : undefined,
  };
}

// ── Conversation detail ──────────────────────────────────────────────────────

function useConversationDetail(id: string | null) {
  const result = useWAConversationDetail(id);
  return {
    data: result.data
      ? ({
          id: result.data.id,
          primaryId: result.data.customerPhone,
          secondaryId: null,
          customerName: result.data.customerName,
          profilePicUrl: null,
          status: result.data.status,
          unreadCount: result.data.unreadCount,
          updatedAt: result.data.updatedAt,
          place: result.data.place,
          person: result.data.person
            ? { id: result.data.person.id, isInterested: result.data.person.isInterested }
            : null,
          lastMessage: result.data.lastMessage,
          isWindowOpen: result.data.isWindowOpen,
          windowExpiresAt: result.data.windowExpiresAt,
          isStarred: result.data.isStarred,
          starredAt: result.data.starredAt,
          isPhoneBlacklisted: result.data.isPhoneBlacklisted ?? false,
          phoneBlacklistReason: result.data.phoneBlacklistReason ?? null,
        } satisfies GenericConversation)
      : undefined,
  };
}

// ── Messages ─────────────────────────────────────────────────────────────────

function useConversationMessages(id: string | null) {
  const result = useWAConversationMessages(id);
  return {
    isLoading: result.isLoading,
    data: result.data
      ? {
          count: result.data.count,
          results: result.data.results.map(
            (m): GenericMessage => ({
              id: m.id,
              conversationId: m.conversationId,
              direction: m.direction,
              messageType: m.messageType,
              senderType: m.senderType,
              content: m.content,
              mediaUrl: m.mediaUrl,
              timestamp: m.timestamp,
              locationName: m.locationName,
              locationAddress: m.locationAddress,
              isTranscribed: m.isTranscribed,
              transcription: m.transcription,
            }),
          ),
        }
      : undefined,
  };
}

// ── Mutations — pass through as-is (signatures are identical) ────────────────

const useSendAdminMessage = useWASendAdminMessage;
const useUpdateConversationStatus = useWAUpdateConversationStatus;
const useMarkConversationRead = useWAMarkConversationRead;
const useToggleInterest = useWAToggleInterest;

function useToggleStar() {
  const mutation = useWAToggleStar();
  return {
    isPending: mutation.isPending,
    mutate: (args: { conversationId: string; isStarred: boolean }) => {
      mutation.mutate(args);
    },
  };
}

// ── Agent configs ────────────────────────────────────────────────────────────

function useAgentConfigs() {
  const result = useWAAgentConfigs();
  return {
    data: result.data as GenericAgentConfig[] | undefined,
  };
}

function useSaveAgentConfig() {
  const mutation = useWASaveAgentConfig();
  return {
    isPending: mutation.isPending,
    mutate: (
      config: GenericAgentConfig & { name: string; systemPrompt: string },
      options?: { onSuccess?: (saved: GenericAgentConfig) => void },
    ) => {
      mutation.mutate(config as Parameters<typeof mutation.mutate>[0], {
        onSuccess: (saved) => options?.onSuccess?.(saved as GenericAgentConfig),
      });
    },
  };
}

function useDeleteAgentConfig() {
  const mutation = useWADeleteAgentConfig();
  return {
    isPending: mutation.isPending,
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      mutation.mutate(id, { onSuccess: options?.onSuccess });
    },
  };
}

// ── Blacklist ─────────────────────────────────────────────────────────────────

function useBlacklistConversation() {
  const mutation = useWABlacklistConversation();
  return {
    isPending: mutation.isPending,
    mutate: (params: { conversationId: string; reason?: string }) => {
      mutation.mutate(params);
    },
  };
}

function useUnblacklistConversation() {
  const mutation = useWAUnblacklistConversation();
  return {
    isPending: mutation.isPending,
    mutate: (params: { conversationId: string }) => {
      mutation.mutate(params);
    },
  };
}

// ── Export as InboxHooks ─────────────────────────────────────────────────────

export const whatsappInboxHooks: InboxHooks = {
  useConversations,
  useConversationDetail,
  useConversationMessages,
  useSendAdminMessage,
  useUpdateConversationStatus,
  useMarkConversationRead,
  useAgentConfigs,
  useSaveAgentConfig,
  useDeleteAgentConfig,
  useToggleInterest,
  useToggleStar,
  useBlacklistConversation,
  useUnblacklistConversation,
};
